"use client";

import { useMotionValue, motion, useMotionTemplate, HTMLMotionProps } from "motion/react";
import React, { MouseEvent as ReactMouseEvent, useState } from "react";
import { CanvasRevealEffect } from "./canvas-reveal-effect";
import { cn } from "../../lib/utils";

export const CardSpotlight = ({
  children,
  radius = 350,
  color = "#262626",
  className,
  theme = "light",
  ...props
}: {
  radius?: number;
  color?: string;
  children: React.ReactNode;
  theme?: "light" | "dark";
} & HTMLMotionProps<'div'>) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: ReactMouseEvent<HTMLDivElement>) {
    let { left, top } = currentTarget.getBoundingClientRect();

    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  const [isHovering, setIsHovering] = useState(false);
  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);
  return (
    <motion.div
      className={cn(
        "group/spotlight p-10 rounded-md relative border border-gray-200 bg-gradient-to-br from-gray-100 via-gray-50 to-white shadow-lg shadow-gray-200",
        className
      )}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <motion.div
        className="pointer-events-none absolute z-0 -inset-px rounded-[inherit] opacity-0 transition duration-300 group-hover/spotlight:opacity-100"
        style={{
          background: theme === "dark" 
            ? "linear-gradient(135deg, #f97316 0%, #f59e0b 50%, #fff7ed 100%)"
            : "linear-gradient(135deg, #14b8a6 0%, #38bdf8 52%, #ecfeff 100%)",
          maskImage: useMotionTemplate`
            radial-gradient(
              ${radius}px circle at ${mouseX}px ${mouseY}px,
              white,
              transparent 80%
            )
          `,
        }}
      >
        {isHovering && (
          <CanvasRevealEffect
            animationSpeed={5}
            containerClassName="bg-transparent absolute inset-0 pointer-events-none"
            colors={theme === "dark" 
              ? [
                  [249, 115, 22],
                  [245, 158, 11],
                ]
              : [
                  [20, 184, 166],
                  [56, 189, 248],
                ]
            }
            dotSize={3}
          />
        )}
      </motion.div>
      {children}
    </motion.div>
  );
};